import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Analytics from '../utils/analytics';
import { GoogleLogin } from '@react-oauth/google';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get('redirect');

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setError('');
        try {
            const response = await api.post('/api/auth/google', {
                credential: credentialResponse.credential
            });
            if (response.ok) {
                const data = await response.json();
                login(data.token, data.user);

                // Analytics: Track standard login event
                Analytics.pushCustomEvent('login', { method: 'google' });

                // If there's a redirect param (e.g. from booking flow), go there
                if (redirectTo) {
                    navigate(redirectTo);
                } else if (data.user.role === "ADMIN") {
                    navigate('/admin');
                } else if (data.user.role === "ARTISAN") {
                    navigate('/artisan/dashboard');
                } else {
                    navigate('/dashboard');
                }
            } else {
                const data = await response.json();
                setError(data.message || 'Google login failed.');
            }
        } catch (err) {
            setError('Google login failed. Please try again.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Client-side validation
        if (!formData.email.trim()) {
            setError('Email address is required.');
            return;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!formData.password) {
            setError('Password is required.');
            return;
        }
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        try {
            const response = await api.post('/api/auth/login', formData);
            if (response.ok) {
                const data = await response.json();
                login(data.token, data.user);

                // Analytics: Track standard login event
                Analytics.pushCustomEvent('login', { method: 'email' });

                // If there's a redirect param (e.g. from booking flow), go there
                if (redirectTo) {
                    navigate(redirectTo);
                } else if (data.user.role === "ADMIN") {
                    navigate('/admin');
                } else if (data.user.role === "ARTISAN") {
                    navigate('/artisan/dashboard');
                } else {
                    navigate('/dashboard');
                }
            } else {
                const data = await response.json();
                setError(data.message || 'Invalid email or password.');
            }
        } catch (err) {
            setError('Invalid email or password. Please try again.');
        }
    };


    // Build signup link preserving redirect param
    const signupLink = redirectTo ? `/signup?redirect=${encodeURIComponent(redirectTo)}` : '/signup';

    return (
        <div className="auth-container">
            <div className="auth-box fade-in-section in-view">
                <h2>Welcome Back</h2>
                <p className="auth-subtitle">Login to your account</p>

                {redirectTo && (
                    <div className="auth-info-banner">
                        🔒 Please log in to continue your booking
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}

                {/* Google login hidden due to key issue - keeping code for future use */
                    false && (
                        <>
                            <div className="social-auth-container">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => setError('Google Login Failed')}
                                    useOneTap
                                    theme="filled_blue"
                                    shape="pill"
                                    width="100%"
                                />
                            </div>

                            <div className="auth-separator">
                                <span>or continue with email</span>
                            </div>
                        </>
                    )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Email Address</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="name@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            placeholder="Enter your password"
                        />
                    </div>

                    <button type="submit" className="btn-primary full-width">Login</button>
                </form>

                <p className="auth-footer">
                    Don't have an account? <Link to={signupLink}>Sign up</Link>
                </p>
            </div>
        </div>
    );
};

export default Login;
